import React from 'react';
import { Box, Typography, Button, Tooltip, Zoom, Divider } from '@mui/material';
import { DynamicIcon } from '../components/icons.jsx';
import { useNotification } from '../context/NotificationContext.jsx';
import { useAuth } from '../context/useAuth';

const GeneralSettings = ({ user }) => {
    const { addNotification } = useNotification();
    const { countryName, timezone } = useAuth();

    const handleCopyId = () => {
        navigator.clipboard.writeText(user?.tenantId || '');
        addNotification("Tenant ID copied to clipboard", "success");
    };

    return (
        <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, md: 4 } }}>
            {/* Header Section */}
            <Box sx={{ mb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography variant="h4" fontWeight="800" sx={{ color: 'text.primary', mb: 1, letterSpacing: '-0.02em' }}>
                        General Settings
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Manage your organization's core identity and regional preferences.
                    </Typography>
                </Box>
                
                <Box 
                    sx={{ 
                        px: 2, 
                        py: 0.75, 
                        bgcolor: user?.tenantStatus === 'Active' ? 'success.soft' : 'warning.soft',
                        color: user?.tenantStatus === 'Active' ? 'success.main' : 'warning.main',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        border: '1px solid',
                        borderColor: user?.tenantStatus === 'Active' ? 'success.outline' : 'warning.outline',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
                    }}
                >
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', animation: 'pulse 2s infinite' }} />
                    {user?.tenantStatus || 'Active'}
                </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' }, gap: 4 }}>
                {/* Identity Section */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Box sx={{ 
                        p: 4, 
                        bgcolor: 'background.paper', 
                        borderRadius: 4, 
                        boxShadow: '0 10px 30px rgba(0,0,0,0.03)', 
                        border: '1px solid', 
                        borderColor: 'divider',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                            <Box sx={{ p: 1, bgcolor: 'primary.soft', borderRadius: 2, color: 'primary.main' }}>
                                <DynamicIcon name="Building2" className="w-5 h-5" />
                            </Box>
                            <Typography variant="h6" fontWeight="700" sx={{ fontSize: '1.1rem' }}>Organization Identity</Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, display: 'block' }}>
                                    Display Name
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, p: 2, bgcolor: 'gray.50', dark: { bgcolor: 'gray.900/30' }, borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                                    <Typography variant="body1" fontWeight="700" sx={{ color: 'text.primary' }}>
                                        {user?.tenantName || user?.tenant?.name || 'My Organization'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, display: 'block' }}>
                                    Tenant Identifier
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                                    <Box sx={{ 
                                        flexGrow: 1,
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 2,
                                        px: 2, 
                                        py: 1.5, 
                                        bgcolor: 'action.hover', 
                                        borderRadius: 3, 
                                        border: '1px solid', 
                                        borderColor: 'divider',
                                        fontFamily: 'JetBrains Mono, monospace',
                                        fontSize: '0.85rem',
                                        color: 'text.primary',
                                        fontWeight: 600
                                    }}>
                                        <DynamicIcon name="Hash" className="w-4 h-4 text-gray-400" />
                                        {user?.tenantId}
                                    </Box>
                                    <Button 
                                        variant="outlined"
                                        onClick={handleCopyId}
                                        sx={{ 
                                            borderRadius: 3, 
                                            textTransform: 'none', 
                                            px: 2.5,
                                            py: 1.25,
                                            fontWeight: 700,
                                            borderColor: 'divider',
                                            color: 'text.primary',
                                            '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.soft', color: 'primary.main' }
                                        }}
                                        startIcon={<DynamicIcon name="Copy" className="w-4 h-4" />}
                                    >
                                        Copy
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Environment & Location Section */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Box sx={{ 
                        p: 4, 
                        bgcolor: 'background.paper', 
                        borderRadius: 4, 
                        boxShadow: '0 10px 30px rgba(0,0,0,0.03)', 
                        border: '1px solid', 
                        borderColor: 'divider'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                            <Box sx={{ p: 1, bgcolor: 'secondary.soft', borderRadius: 2, color: 'secondary.main' }}>
                                <DynamicIcon name="Globe2" className="w-5 h-5" />
                            </Box>
                            <Typography variant="h6" fontWeight="700" sx={{ fontSize: '1.1rem' }}>Regional Settings</Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'gray.50', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'divider' }}>
                                        <DynamicIcon name="MapPin" className="w-5 h-5 text-gray-500" />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>Region</Typography>
                                        <Typography variant="body1" fontWeight="700">{countryName || 'US-East (Virginia)'}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'gray.50', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'divider' }}>
                                        <DynamicIcon name="Clock" className="w-5 h-5 text-gray-500" />
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>Timezone</Typography>
                                        <Typography variant="body1" fontWeight="700" sx={{ color: 'primary.main' }}>{timezone || 'UTC'}</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider sx={{ borderStyle: 'dashed' }} />

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'primary.soft', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'primary.outline' }}>
                                        <DynamicIcon name="Crown" className="w-5 h-5 text-primary-main" />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>Service Plan</Typography>
                                        <Typography variant="body1" fontWeight="800" sx={{ color: 'primary.main', fontSize: '1.2rem' }}>{user?.planName || 'Pro'}</Typography>
                                    </Box>
                                </Box>
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 0.75, 
                                        px: 1.5, 
                                        py: 0.5, 
                                        bgcolor: 'success.soft', 
                                        borderRadius: '8px', 
                                        color: 'success.main',
                                        border: '1px solid',
                                        borderColor: 'success.outline'
                                    }}
                                >
                                    <DynamicIcon name="ShieldCheck" className="w-4 h-4" />
                                    <Typography variant="caption" fontWeight="800">ACTIVE</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default GeneralSettings;
